import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Download, Filter, Music, AlertCircle, CheckCircle } from 'lucide-react';
import { getAllPlays } from '../../services/playsService';
import {
  getMissingAudioLines,
  getAudioStatistics,
  getPlayStructure,
  getCharactersList,
  exportToCSV,
  downloadCSV
} from '../../services/audioManagementService';
import StatCard from './components/StatCard';
import FilterBar from './components/FilterBar';
import AudioLineTable from './components/AudioLineTable';

const AudioFileManagement = () => {
  const [plays, setPlays] = useState([]);
  const [missingLines, setMissingLines] = useState([]);
  const [filteredLines, setFilteredLines] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    playId: '',
    act: '',
    scene: '',
    character: '',
    searchQuery: ''
  });
  
  const [availableFilters, setAvailableFilters] = useState({
    acts: [],
    scenes: [],
    characters: []
  });

  // Selected lines for bulk operations
  const [selectedLines, setSelectedLines] = useState(new Set());

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Update filtered lines when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, missingLines]);

  // Update available filter options when play changes
  useEffect(() => {
    if (filters?.playId) {
      updateFilterOptions(filters?.playId);
    } else {
      setAvailableFilters({ acts: [], scenes: [], characters: [] });
    }
  }, [filters?.playId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch plays
      const playsData = await getAllPlays();
      setPlays(playsData || []);

      // Fetch statistics
      const stats = await getAudioStatistics();
      setStatistics(stats);

      // Fetch all missing audio lines
      const lines = await getMissingAudioLines();
      setMissingLines(lines || []);
      setFilteredLines(lines || []);

      // Fetch all characters for filter
      const characters = await getCharactersList();
      setAvailableFilters(prev => ({ ...prev, characters }));

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load audio management data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateFilterOptions = async (playId) => {
    try {
      const structure = await getPlayStructure(playId);
      const characters = await getCharactersList(playId);
      
      setAvailableFilters({
        acts: structure?.acts,
        scenes: structure?.scenes,
        characters
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...missingLines];

    // Apply play filter
    if (filters?.playId) {
      filtered = filtered?.filter(line => line?.play_id === filters?.playId);
    }

    // Apply act filter
    if (filters?.act) {
      filtered = filtered?.filter(line => line?.act === parseInt(filters?.act));
    }

    // Apply scene filter
    if (filters?.scene) {
      filtered = filtered?.filter(line => line?.scene === parseInt(filters?.scene));
    }

    // Apply character filter
    if (filters?.character) {
      filtered = filtered?.filter(line => 
        line?.speaker?.toLowerCase()?.includes(filters?.character?.toLowerCase())
      );
    }

    // Apply search query
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(line =>
        line?.id?.toLowerCase()?.includes(query) ||
        line?.speaker?.toLowerCase()?.includes(query) ||
        line?.line_text?.toLowerCase()?.includes(query)
      );
    }

    setFilteredLines(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
      // Reset dependent filters
      ...(filterName === 'playId' && { act: '', scene: '', character: '' })
    }));
  };

  const clearFilters = () => {
    setFilters({
      playId: '',
      act: '',
      scene: '',
      character: '',
      searchQuery: ''
    });
    setSelectedLines(new Set());
  };

  const handleSelectLine = (lineId) => {
    setSelectedLines(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(lineId)) {
        newSet?.delete(lineId);
      } else {
        newSet?.add(lineId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedLines?.size === filteredLines?.length) {
      setSelectedLines(new Set());
    } else {
      setSelectedLines(new Set(filteredLines.map(line => line.id)));
    }
  };

  const handleExportCSV = () => {
    try {
      const linesToExport = selectedLines?.size > 0
        ? filteredLines?.filter(line => selectedLines?.has(line?.id))
        : filteredLines;

      const csv = exportToCSV(linesToExport);
      const timestamp = new Date()?.toISOString()?.split('T')?.[0];
      const filename = `missing_audio_${timestamp}.csv`;
      
      downloadCSV(csv, filename);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleUploadSuccess = (lineId) => {
    // Refresh data after successful upload
    fetchInitialData();
    
    // Show success notification
    console.log(`Audio uploaded successfully for line: ${lineId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading audio management data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={fetchInitialData}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Audio File Management - BardPlay Admin</title>
        <meta name="description" content="Manage and track missing audio files across all Shakespeare plays" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Music className="w-8 h-8 text-purple-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Audio File Management
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Track and prioritize missing audio recordings
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleExportCSV}
                disabled={filteredLines?.length === 0}
                className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
                {selectedLines?.size > 0 && (
                  <span className="bg-purple-800 px-2 py-0.5 rounded-full text-xs">
                    {selectedLines?.size}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Lines"
              value={statistics?.totalLines || 0}
              icon={Music}
              color="blue"
            />
            <StatCard
              title="Missing Audio"
              value={statistics?.missingAudio || 0}
              icon={AlertCircle}
              color="red"
            />
            <StatCard
              title="Completed"
              value={statistics?.completedAudio || 0}
              icon={CheckCircle}
              color="green"
            />
            <StatCard
              title="Completion Rate"
              value={`${statistics?.completionPercentage || 0}%`}
              icon={CheckCircle}
              color="purple"
            />
          </div>

          {/* Filters */}
          <FilterBar
            filters={filters}
            availableFilters={availableFilters}
            plays={plays}
            onFilterChange={handleFilterChange}
            onClearFilters={clearFilters}
          />

          {/* Results Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{filteredLines?.length}</span> lines
                  {selectedLines?.size > 0 && (
                    <span className="ml-2">
                      · <span className="font-semibold text-purple-600">{selectedLines?.size}</span> selected
                    </span>
                  )}
                </p>
              </div>
              
              {filteredLines?.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  {selectedLines?.size === filteredLines?.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <AudioLineTable
            lines={filteredLines}
            selectedLines={selectedLines}
            onSelectLine={handleSelectLine}
            onUploadSuccess={handleUploadSuccess}
          />

          {/* Empty State */}
          {filteredLines?.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {filters?.playId || filters?.act || filters?.scene || filters?.character || filters?.searchQuery
                  ? 'No Missing Audio Found' :'All Audio Files Complete!'}
              </h3>
              <p className="text-gray-600 mb-4">
                {filters?.playId || filters?.act || filters?.scene || filters?.character || filters?.searchQuery
                  ? 'Try adjusting your filters to see more results.' :'All lines have audio recordings. Great work!'}
              </p>
              {(filters?.playId || filters?.act || filters?.scene || filters?.character || filters?.searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AudioFileManagement;